import Vue from 'vue';
import KEYS from '../../../values/keys';
import EdPdfViewer from '@/components/common/ed-pdf-viewer/ed-pdf-viewer.vue';
import EdHtmlViewer from '@/components/common/ed-html-viewer/ed-html-viewer.vue';
import { SECTION_TYPES, SPLIT_SUTBTYPES } from '../../values/sections';
import { COURSE_EVENTS } from '../../values/events';
import { COLORS_BASE  } from '@/core/values/core.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import { HOMEWORK_STATUS } from '@/homework/values/homeworks.values';
import EdTeacherMeetingTransparency from '@/core/components/ed-meeting-transparency/ed-teacher-meeting-transparency/ed-teacher-meeting-transparency.component.vue';
import EdStudentMeetingTransparency from '@/core/components/ed-meeting-transparency/ed-student-meeting-transparency/ed-student-meeting-transparency.component.vue';
import { MY_CONTENT_TYPES } from '@/app/private/my-content/values/my-content.values';
import { ScreenMirroringMixin } from '@/screen-mirroring/mixins/screen-mirroring.mixin';
import { _MEETINGS_STORE } from '@/meetings/values/meetings.values';

const sizesByDefault = { h: '100%', w: '100%' };


/*
This class has two modes to load a pdf file and these mode are exclusive:

1) "urlMaterialDisplay" attribute mode: when present, it defines the pdf file to be loaded without making any second pdfurl validation. 
2) "section" attribute mode: when present, it is used to ask backend service if this section already has a pdfurl document.
    otherwise it will ask to the backend every 15 seconds if section has its pdfurl file version. 
*/
export default Vue.component('ed-file-viewer', {
  name: 'ed-file-viewer',
  props: {
    'section': {},
    'height': {},
    'width': {},
    'sectionMaterial': {},
    'urlMaterialDisplay': {},
    'fileType': {},
    'footerPages': {},
    'showNotice': {},
    'initPage': {},
    'courseId': {},
    'isPresentationFile': {},
    'documentIndex': {
      type: Number,
      default: 0
    },
    'isVisor': {}
  },
  components: {
    EdPdfViewer,
    EdHtmlViewer,
    EdTeacherMeetingTransparency,
    EdStudentMeetingTransparency
  },
  mixins: [CourseToolsMixin, ScreenMirroringMixin],
  data: () => {
    return {
      isFullScreen: false,
      epubViewer: null,
      url: '',
      h: sizesByDefault.h,
      w: sizesByDefault.w,
      sectionId: 0,
      chapterStart: [],
      isProcessing: false,
      preId: 'pdf-',
      isPdfLoading: true,
      sectionUpdated: null,
      sectionUpdateTimeoutId: null,
    }
  },
  computed: {
    classes() {
      return {
        'ed-file-viewer--full-screen': this.isFullScreen
      }
    },
    hasContent() {

      if( !this.section && !this.sectionMaterial ) {
        return false;
      }

      if(this.section.type != SECTION_TYPES.LESSON_MATERIAL.code) {
        return this.section.text ? true : false;
      } else {        
        return this.sectionMaterial.data.pdfUrl || this.urlMaterialDisplay ? true : false;
      }
    },
    isFileReady() {
      return (this.urlMaterialDisplay || this.section?.assetsInfo[0].pdfUrl) ? true : false;
    },
    isPresentationTeacherMode() {
      return this.$store.state.CourseStore.isPresentationTeacherMode;
    },
    isPresentationStudentMode() {
      return this.$store.state.CourseStore.isPresentationStudentMode;
    },
    isWhiteboardTransparency() {
      return this.$store.state.MeetingsStore.isWhiteboardTransparency;
    },
    courseSelected() {
      return this.$store.state.CourseStore.presentationCourseSelected;
    },
    isSameCourse() {
      return this.courseSelected === this.section.courseId || (this.sectionMaterial && this.courseSelected === this.sectionMaterial.courseId);
    },
    documentHash() {
      if( this.urlMaterialDisplay ) {
        //example of this.urlMaterialDisplay value
        //https://eduac.local:8343/public/api/media/store/6bff175544c1ec773df27bd0cf412bc2fb2029301d7b65fbe40c60c69d62ef67#toolbar=0&navpanes=0&scrollbar=0&page=
        return this.urlMaterialDisplay.split('/').pop().match(/[A-Z0-9]+/ig)[0];
      } else if (this.section.type === SECTION_TYPES.LESSON_TEXT.code) {
        const hash = this.section.assetsInfo?.[0]?.hash ?? (this.section.text ? JSON.parse(this.section.text)?.asset ?? null : null);
        return hash;
      } else if (this.section.type !== SECTION_TYPES.LESSON_MATERIAL.code && this.section.type !== MY_CONTENT_TYPES.BOOK) {
        return this.section.text;
      } else {        
        return this.sectionMaterial.data.pdfHash;
      }
    },
    documentName() {
      return this.section?.name;
    },
    currentCourseId() {
      return this.section?.courseId || this.sectionMaterial?.courseId;
    },
    isSameUser() {
      return this.currentBoardUserId === this.userId;
    },
    isTeacherScreenUser() {
      return this.$store.state.MeetingsStore.currentBoardUser.isTeacher;
    },
    isBoardSectionHomework() {
      return this.$store.state.CourseStore.isBoardSectionHomework;
    },
    isHtmlFile() {
      if( this.section?.assetsInfo ) {
        const isHtml = this.section.assetsInfo.reduce( (acc, asset ) => asset.directory && asset.pdfUrl != null, true)
        if(!isHtml && this.sectionUpdated?.assetsInfo) {
          return this.sectionUpdated.assetsInfo.reduce( (acc, asset ) => asset.directory && asset.pdfUrl != null, false);
        }
        return isHtml;
      }
      return false;
    }  
  },
  methods: {
    setSizes(w = sizesByDefault.w, h = sizesByDefault.h) {
      this.h = h;
      this.w = w;
    },
    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
      this.$edEventBus.$emit(COURSE_EVENTS.SET_OBJECT_FULL_SCREEN, this.isFullScreen);
      if(this.isFullScreen) {
        this.setSizes('100%', '100%');
        this.$App.Store.setCopyrightsColor(COLORS_BASE.LIGHT);
        this.$App.Store.setHeaderVisible(false);
      } else {
        this.setSizes(this.width, this.height);
        this.$App.Store.setCopyrightsColor(COLORS_BASE.COMMON);
        this.$App.Store.setHeaderVisible(true);
      }
      if(this.epubViewer) {
        setTimeout(() => {
          this.epubViewer.resize(this.w, this.h);
        }, 100);
      }
    },
    setEpubViewer(epubFile) {
      this.$refs.EdLoading.show();
      var book = ePub(epubFile);
      this.epubViewer = book.renderTo('epub-viewer', {flow: 'paginated', width: this.w, height: this.h, view: 'iframe'});
      this.epubViewer.display().finally(()=>this.$refs.EdLoading.hide());
      if(this.section.type == SECTION_TYPES.LESSON_MATERIAL.code) {
        book.ready.then(() => {
          setTimeout( () => {
            if (this.section.sectionMaterial.startSection !== 'all') {
              let hrefLabelSection = decodeURIComponent(this.section.sectionMaterial.startSection);
              this.epubViewer.display(hrefLabelSection.replace('../',''));
            }
          }, 2000);
        });
      }
    },
    getIsResponse(pdfUrl) {
      if(this.isVisor) {
        switch(this.section.type) {
          case SECTION_TYPES.LESSON_PDF.code:
          case SECTION_TYPES.LESSON_DOC.code:
          case SECTION_TYPES.LESSON_DOCX.code:
          case SECTION_TYPES.LESSON_XLS.code:
          case SECTION_TYPES.LESSON_XLSX.code:
            return pdfUrl;
          default: 
            break;
        } 
      }
      if(this.section.homeworkInfo) {
        switch(this.section.homeworkStatus) {
          case HOMEWORK_STATUS.FINISHED:
          case HOMEWORK_STATUS.GRADED:
          case HOMEWORK_STATUS.FEEDBACK_DRAFT:
          case HOMEWORK_STATUS.NEW: 
          case HOMEWORK_STATUS.SEEN:
            //For responses by board url field and upload responses pdfUrl field
            if(this.section.homeworkInfo.homeworkAssetInfo.pdfUrl) {
              return this.section.homeworkInfo.homeworkAssetInfo.pdfUrl
            } 
            return this.section.homeworkInfo.homeworkAssetInfo.url;
        }
      }
      return pdfUrl || this.section?.homeworkAssetInfo?.url;
    },
    setViewer() {
      
      //when pdf url comes from materials and subject library (page and section)
      if( this.urlMaterialDisplay ) {
        this.url = this.urlMaterialDisplay;  

      //when pdf url comes from section objects and homeworks 
      } else {
        const pdfUrl = (this.sectionUpdated || this.section).assetsInfo[0].pdfUrl;
        this.url = this.getIsResponse( pdfUrl );
      }
      
      this.isProcessing = this.url ?  false : true;
    },
    nextPage() {
      this.epubViewer.next();
    },
    prevPage() {
      this.epubViewer.prev();
    },
    resizeContainer() {
      if(this.epubViewer) this.epubViewer.resize(this.w, this.h);
    },
    setPdfLoading(value) {
      this.isPdfLoading = value;
    },
    loadingPage(pageData: {height: number, width: number, page: number}) : void {
      const transparencyRef = this.$refs.teacherTransparency || this.$refs.studentTransparency;
      if(transparencyRef) {
        transparencyRef.loadingPage(pageData);
      }
    },
    refreshViewer() {
      this.setViewer();
      if(this.isProcessing && !this.sectionMaterial) {
        let fileData = { 
          sectionId: this.section.sectionId || this.section.id, 
          materialId: null,
          courseId: this.section.courseId || this.courseId, 
          classId: this.section.classId
        };
        const timeoutInMiliseconds = 15000;
        this.sectionUpdateTimeoutId = setTimeout( async () => {
          if( this.isProcessing ) {
            this.sectionUpdated = await this.$Courses.Store.updateCourseFilesIfInfoReadyA(fileData);
            if(!SPLIT_SUTBTYPES.includes(this.sectionUpdated.type)){
              this.setSelectedData(this.sectionUpdated);
            }
            this.refreshViewer();
          }
        }, timeoutInMiliseconds);
      }
    },
    setSelectedData(sectionUpdated) {
      if(!this.section.assetsInfo[0].pdfUrl && sectionUpdated.assetsInfo[0].pdfUrl) {
        this.$Courses.Store.setPreviewSelected({
          section: sectionUpdated,
          material: null,
          courseId: this.section.courseId || this.courseId,  
          classId: this.section.classId
        });
      }
    },
    async listenSocketEventWhenAssetIsConverted(params:{username:string, entityId:string, entityType:string}) {  
      if( params.entityType === 'SECTION' && this.section?.sectionId === params.entityId ) {
        clearTimeout(this.sectionUpdateTimeoutId);
        let fileData = { 
          sectionId: this.section.sectionId || this.section.id, 
          materialId: null,
          courseId: this.section.courseId || this.courseId,  
          classId: this.section.classId
        };
        this.sectionUpdated = await this.$Courses.Store.updateCourseFilesIfInfoReadyA(fileData);
        this.refreshViewer();
      }
    },
  },
  created() {
    if(!this.sectionMaterial) {
      this.isProcessing = !this.isFileReady;
    } else {
      this.isProcessing = this.showNotice;
    }
  },
  mounted() {
    this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, 0);
    this.$edEventBus.$on(COURSES_EVENTS.COURSE_WIDGET_RESIZED, this.resizeContainer);
    this.setSizes(this.width, this.height);
    this.refreshViewer();
    this.$edEventBus.$on(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash), this.setPdfLoading);
    this.$edEventBus.$on(COURSES_EVENTS.ASSET_CONVERTED, this.listenSocketEventWhenAssetIsConverted);
  },
  beforeDestroy(){
    clearTimeout(this.sectionUpdateTimeoutId);
    this.$edEventBus.$off(COURSE_EVENTS.IS_PDF_LOADING_TRANSPARENCY.replace('documentHash', this.documentHash));
    this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, 0);
    this.$edEventBus.$off(COURSES_EVENTS.COURSE_WIDGET_RESIZED, this.resizeContainer);
    this.$edEventBus.$off(COURSES_EVENTS.ASSET_CONVERTED, this.listenSocketEventWhenAssetIsConverted);
  }
});